import React, { useState, useMemo } from 'react';
import MaterialIcon from '../ui/MaterialIcon';
import FilterChips from '../ui/FilterChips';
import BentoCard from '../ui/BentoCard';
import transferPartners from '../../data/transferPartners';

const BANK_CHIPS = [
  { id: 'all', label: 'All Banks' },
  { id: 'Chase', label: 'Chase' },
  { id: 'Amex', label: 'Amex' },
  { id: 'Citi', label: 'Citi' },
  { id: 'Capital One', label: 'Capital One' },
];

function TransferReferenceScreen({ onBack }) {
  const [activeBank, setActiveBank] = useState('all');

  const filteredPartners = useMemo(() => {
    if (activeBank === 'all') return transferPartners;
    return transferPartners.filter((partner) => partner.bank === activeBank);
  }, [activeBank]);

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-surface-container-high transition-colors"
          aria-label="Go back"
        >
          <MaterialIcon name="arrow_back" size={24} className="text-on-surface" />
        </button>
        <h1 className="text-2xl font-bold text-on-surface">Transfer Partners</h1>
      </div>

      {/* Filter Chips */}
      <FilterChips
        chips={BANK_CHIPS}
        activeChip={activeBank}
        onChipClick={setActiveBank}
      />

      {/* Transfer Partner Cards Grid */}
      <div className="grid grid-cols-2 gap-3">
        {filteredPartners.map((partner) => (
          <BentoCard key={partner.id} className="relative overflow-hidden">
            {/* Bonus badge */}
            {partner.bonus && (
              <div className="absolute top-3 right-3">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-success-light text-success">
                  {partner.bonus}
                </span>
              </div>
            )}

            {/* Airline name */}
            <h3 className="text-sm font-semibold text-on-surface pr-16 leading-tight">
              {partner.airline}
            </h3>

            {/* Program name */}
            <p className="text-xs text-on-surface-variant mt-1">{partner.program}</p>

            {/* Transfer ratio */}
            <div className="flex items-center gap-1.5 mt-3">
              <MaterialIcon name="swap_horiz" size={16} className="text-primary" />
              <span className="text-sm font-semibold text-primary">{partner.ratio}</span>
            </div>

            {/* Bank badge */}
            <div className="mt-3">
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold text-white"
                style={{ backgroundColor: partner.bankColor }}
              >
                {partner.bank}
              </span>
            </div>
          </BentoCard>
        ))}
      </div>

      {/* Empty state */}
      {filteredPartners.length === 0 && (
        <div className="text-center py-12">
          <MaterialIcon name="search_off" size={48} className="text-on-surface-variant/40" />
          <p className="text-sm text-on-surface-variant mt-3">No transfer partners found for this bank.</p>
        </div>
      )}

      {/* Editorial Insight */}
      <BentoCard className="bg-gradient-to-br from-warning/5 to-warning/10">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-warning-light flex items-center justify-center">
            <MaterialIcon name="lightbulb" size={22} className="text-warning" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-on-surface mb-1">Editorial Insight</h3>
            <p className="text-sm text-on-surface-variant leading-relaxed">
              Transfer ratios can change. Always verify current rates before initiating transfers.
            </p>
          </div>
        </div>
      </BentoCard>
    </div>
  );
}

export default TransferReferenceScreen;
